/**
 * Sessions, sign-in and the one place a request is made. Under server authority (D1)
 * there is no save to push: the world lives on the server and the client sends intents
 * through `apiRequest` below.
 *
 * Identity is a session cookie the server sets (D8). The client never invents a
 * user id: it holds a device id, asks `POST /auth/guest` for a session on first
 * load, and upgrades that same guest into a real account with a passkey or a
 * magic link. Every call sends credentials so the cookie travels.
 */
import type { DeleteWorldResponse, WorldsResponse } from '@hedgerow/sim';
import { now } from '../clock.ts';
import {
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import {
  SIGNIN_ADDRESS_REFUSED,
  SIGNIN_CODE_REFUSED,
  SIGNIN_CODE_SENT,
  SIGNIN_MAINTENANCE,
  SIGNIN_NO_CONNECTION,
  SIGNIN_PASSKEY_NOT_CHOSEN,
  SIGNIN_PASSKEY_START_FAILED,
  SIGNIN_PASSKEY_UNSUPPORTED,
  SIGNIN_START_FAILED,
  SIGNIN_TOO_MANY_CODES,
  SIGNIN_TOO_MANY_LINKS,
  signinPasskeyWrongAddress,
} from './copy.ts';

/** Same origin in the container, where nginx proxies /api to the Worker. */
const API = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

const DEVICE_KEY = 'hedgerow-device';

/**
 * Whether a passkey or an email has ever been used on this device (D190). It sits beside
 * the device id because it is the same kind of fact: something about this browser, not
 * about the account. Once set, a lost session opens the sign-in sheet rather than minting
 * a guest, because a guest world on a signed-in device is a trap.
 */
const SIGNED_IN_KEY = 'hedgerow-signed-in';

/**
 * How long one request may go unanswered before it is treated as a request that failed
 * (D153). `fetch` does not reject when a server accepts the connection and then says
 * nothing at all, so without a deadline a single silent request waits for ever and boot
 * never leaves "Opening the world". Twenty seconds is deliberately generous: a slow phone
 * on a slow network is answered well inside it, so this fires for a server that is not
 * answering rather than for one that is merely slow.
 */
export const REQUEST_TIMEOUT_MS = 20000;

/** How long to wait on Turnstile, both for its script and for the token it hands back. */
const TURNSTILE_WAIT_MS = 15000;

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private windows have no storage. Sync degrades to off; the game is fine.
  }
}

/**
 * The device id the guest account is bound to. It is not an identity on its own:
 * the server decides which user it belongs to and answers with a session cookie.
 */
/**
 * A v4 uuid. `crypto.randomUUID` is only exposed on secure origins, and the local stack
 * on the LAN is plain http, so fall back to a Math.random shape rather than throw and
 * leave the app on "Opening the world" for ever.
 */
export function uuid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.floor(Math.random() * 16);
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }
}

export function deviceId(): string {
  const existing = read(DEVICE_KEY);
  if (existing) return existing;
  const id = uuid();
  write(DEVICE_KEY, id);
  return id;
}

/** Has a passkey or an email ever signed this device in (D190)? */
export function hasSignedIn(): boolean {
  return read(SIGNED_IN_KEY) === '1';
}

/** Called wherever a passkey or an email sign-in succeeds. */
function rememberSignedIn(): void {
  write(SIGNED_IN_KEY, '1');
}

/**
 * Signing out is the player choosing to be a guest again, so the device stops being one
 * that has been signed in. Only a deliberate sign-out clears this; a session that merely
 * ended does not.
 */
function forgetSignedIn(): void {
  try {
    localStorage.removeItem(SIGNED_IN_KEY);
  } catch {
    // Private windows have no storage. Nothing was remembered, so nothing to forget.
  }
}

/**
 * What the last request told the client about the server, beyond plain success (D3, nginx's
 * `@api_down` in `infra/nginx-locations.inc`).
 *
 * - `offline`: the fetch itself threw. No network, or no server in this deployment.
 * - `maintenance`: nginx answered 502, 503 or 504 because the API container is not up
 *   (a deploy, a crash). The world is safe on the server; it just cannot be reached yet.
 * - `signed-out`: the server answered 401. The session cookie is gone or expired.
 * - `error`: some other 5xx. Not offline, not a deploy, not a session problem.
 * - `ok`: 2xx or an expected 4xx (400, 404, 429) that the caller handles itself.
 */
export type ServerStatus = 'ok' | 'offline' | 'maintenance' | 'signed-out' | 'error';

export interface ServerStatusEvent {
  status: ServerStatus;
  /** The injected clock's time (`clock.ts`) when this was classified. */
  at: number;
}

/** A response's HTTP status, classified. Pure, so it can be tested on a fake. */
export function classifyStatus(status: number): ServerStatus {
  if (status === 401) return 'signed-out';
  if (status === 502 || status === 503 || status === 504) return 'maintenance';
  if (status >= 500) return 'error';
  return 'ok';
}

let lastStatus: ServerStatusEvent = { status: 'ok', at: 0 };
const statusListeners = new Set<(e: ServerStatusEvent) => void>();

function setServerStatus(status: ServerStatus): void {
  lastStatus = { status, at: now() };
  for (const listener of statusListeners) listener(lastStatus);
}

/** Every classified outcome of an `apiRequest` call, as it happens. */
export function onServerStatus(cb: (e: ServerStatusEvent) => void): () => void {
  statusListeners.add(cb);
  return () => statusListeners.delete(cb);
}

/**
 * Every request the app makes. Returns null when nothing answered, which is how the
 * client tells "offline" from "the server said no" (D3). Also classifies and broadcasts
 * what happened, so every screen agrees on offline vs maintenance vs a
 * session that has ended without each re-deriving it from a status code.
 *
 * Every request has a deadline (D153). A server that never answers is the same to the
 * player as one that refused: null back, offline broadcast, and the retry that already
 * exists. A timeout is offline rather than maintenance because silence says nothing about
 * why: maintenance is what nginx answers, in words, when the API is known to be down.
 */
export async function apiRequest(path: string, init: RequestInit = {}): Promise<Response | null> {
  const deadline = new AbortController();
  const timer = setTimeout(() => deadline.abort(), REQUEST_TIMEOUT_MS);
  // The caller's own signal still aborts: the deadline composes with it, never replaces it.
  const asked = init.signal ?? undefined;
  const relay = () => deadline.abort();
  if (asked) {
    if (asked.aborted) deadline.abort();
    else asked.addEventListener('abort', relay);
  }
  let res: Response;
  try {
    res = await fetch(`${API}${path}`, {
      ...init,
      signal: deadline.signal,
      // The session cookie is the whole identity, so it has to travel even when
      // the Worker is on another origin, which it is under `vite dev`.
      credentials: 'include',
      headers: { 'content-type': 'application/json', ...init.headers },
    });
  } catch {
    // An abort the caller asked for is not news about the server, so it is not broadcast;
    // anything else, the deadline included, is offline. Local play continues either way.
    if (!asked?.aborted) setServerStatus('offline');
    return null;
  } finally {
    // A slow request that was answered must not be aborted late.
    clearTimeout(timer);
    asked?.removeEventListener('abort', relay);
  }
  setServerStatus(classifyStatus(res.status));
  return res;
}

const call = apiRequest;

interface PostOutcome<T> {
  ok: boolean;
  status: ServerStatus;
  body: T | null;
}

async function post<T>(path: string, body?: unknown): Promise<PostOutcome<T>> {
  const init: RequestInit = { method: 'POST' };
  if (body !== undefined) init.body = JSON.stringify(body);
  const res = await call(path, init);
  if (!res) return { ok: false, status: 'offline', body: null };
  const status = classifyStatus(res.status);
  if (!res.ok) return { ok: false, status, body: null };
  return { ok: true, status, body: (await res.json()) as T };
}

/** No connection, or the server is mid-deploy: the two cases sign-in copy tells apart. */
function startFailureMessage(status: ServerStatus, fallback: string): string {
  if (status === 'offline') return SIGNIN_NO_CONNECTION;
  if (status === 'maintenance') return SIGNIN_MAINTENANCE;
  return fallback;
}

// --- Turnstile ----------------------------------------------------------------

interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      size: string;
      callback: (token: string) => void;
      'error-callback': () => void;
    },
  ) => string;
}

let turnstileScript: Promise<void> | null = null;

function loadTurnstile(): Promise<void> {
  if (turnstileScript) return turnstileScript;
  turnstileScript = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('turnstile did not load'));
    document.head.appendChild(script);
  });
  return turnstileScript;
}

/**
 * A Turnstile token, or undefined when no site key is configured, which is the
 * local case. The server skips verification in exactly the same case.
 */
async function turnstileToken(): Promise<string | undefined> {
  if (!TURNSTILE_SITE_KEY) return undefined;
  try {
    // A script tag whose load never lands fires neither onload nor onerror, which would
    // leave boot waiting exactly as a silent request did (D153). Give it the same wait
    // the token itself gets, then carry on without a token.
    const loaded = await Promise.race([
      loadTurnstile().then(() => true),
      new Promise<boolean>((resolve) => setTimeout(() => resolve(false), TURNSTILE_WAIT_MS)),
    ]);
    if (!loaded) return undefined;
    const api = (globalThis as { turnstile?: TurnstileApi }).turnstile;
    if (!api) return undefined;
    const holder = document.createElement('div');
    holder.style.display = 'none';
    document.body.appendChild(holder);
    return await new Promise<string | undefined>((resolve) => {
      const done = (token?: string) => {
        holder.remove();
        resolve(token);
      };
      const timer = setTimeout(() => done(undefined), TURNSTILE_WAIT_MS);
      api.render(holder, {
        sitekey: TURNSTILE_SITE_KEY,
        size: 'invisible',
        callback: (token: string) => {
          clearTimeout(timer);
          done(token);
        },
        'error-callback': () => {
          clearTimeout(timer);
          done(undefined);
        },
      });
    });
  } catch {
    return undefined;
  }
}

// --- Accounts -----------------------------------------------------------------

export interface Account {
  id: string;
  displayName: string;
  email: string | null;
  isGuest: boolean;
}

export interface SignInResult {
  ok: boolean;
  /** Copy the caller shows the player. British English, no exclamation marks. */
  message: string;
  account?: Account;
  merged?: boolean;
  archivedWorldId?: string | null;
  /** A magic link handed straight back by the local stack, where no mail is sent. */
  link?: string;
  /** The sign-in code, handed back the same way and on the local stack only. */
  code?: string;
}

/**
 * Why a passkey ceremony failed, in words. A SecurityError means the address bar does
 * not match the relying party, which is what happens on the LAN when the page is opened
 * by IP address rather than by the name the server was told.
 */
function passkeyFailure(err: unknown, rpId: string | undefined, fallback: string): string {
  const name = err instanceof Error ? err.name : '';
  if (name === 'SecurityError' && rpId) {
    return signinPasskeyWrongAddress(rpId, location.port ? `:${location.port}` : '');
  }
  if (name === 'NotAllowedError' || name === 'AbortError') return fallback;
  if (name === 'NotSupportedError') return SIGNIN_PASSKEY_UNSUPPORTED;
  return name ? `${fallback} (${name})` : fallback;
}

/**
 * What `/auth/me` said, in three parts rather than two (D190).
 *
 * - `account`: the server named a user. This device has a session.
 * - `no-session`: the server answered, and said this device has no session. A 200 with no
 *   user, or a 401, which is the API itself saying the cookie no longer holds.
 * - `unreachable`: nothing answered, or what answered says nothing about the device. A
 *   deploy's 503, a proxy's 502, D153's deadline, a body that will not parse.
 *
 * Only `no-session` is an answer about the device, so only `no-session` may mint a guest.
 * Treating `unreachable` as "you have never played" is what lost Sam his account: the
 * client asked for a guest, the server set a fresh cookie, and the real session id on the
 * phone was overwritten during a twenty-second restart window.
 */
export type SessionLookup =
  { kind: 'account'; account: Account } | { kind: 'no-session' } | { kind: 'unreachable' };

/** Who this device's session belongs to, or why that could not be established (D190). */
export async function me(): Promise<SessionLookup> {
  const res = await call('/auth/me');
  if (!res) return { kind: 'unreachable' };
  // A 401 is the API's own answer that the cookie no longer holds, not silence about it.
  if (res.status === 401) return { kind: 'no-session' };
  if (!res.ok) return { kind: 'unreachable' };
  const body = (await res.json().catch(() => null)) as { user?: Account | null } | null;
  if (!body) return { kind: 'unreachable' };
  return body.user ? { kind: 'account', account: body.user } : { kind: 'no-session' };
}

/** The account this device holds a session for, or null. For screens that only need that. */
export async function currentAccount(): Promise<Account | null> {
  const lookup = await me();
  return lookup.kind === 'account' ? lookup.account : null;
}

/**
 * What boot and a 401 get back from `ensureSession` (D190).
 *
 * - `account`: there is a session, minted or resumed.
 * - `needs-sign-in`: the server said there is no session, and a passkey or an email has
 *   been used on this device. Minting a guest here would hand the player someone else's
 *   empty world, so the sign-in sheet is opened instead.
 * - `unreachable`: nothing was learnt and nothing was minted. This lands on the boot retry
 *   D153 and D156 already built.
 */
export type SessionOutcome =
  { kind: 'account'; account: Account } | { kind: 'needs-sign-in' } | { kind: 'unreachable' };

/**
 * Called once on load, before anything else touches the server. A device that has
 * played before gets its own account back; a new one gets a guest account.
 *
 * A guest is minted only when the server has successfully said this device has no session,
 * and only when no passkey or email has ever been used here (D190).
 */
export async function ensureSession(): Promise<SessionOutcome> {
  const current = await me();
  if (current.kind === 'account') return current;
  if (current.kind === 'unreachable') return { kind: 'unreachable' };
  if (hasSignedIn()) return { kind: 'needs-sign-in' };
  const token = await turnstileToken();
  const outcome = await post<{ user: Account }>('/auth/guest', {
    deviceId: deviceId(),
    ...(token ? { turnstileToken: token } : {}),
  });
  const user = outcome.body?.user;
  return user ? { kind: 'account', account: user } : { kind: 'unreachable' };
}

export async function signOut(): Promise<void> {
  await post('/auth/logout');
  forgetSignedIn();
}

/**
 * D417: delete the account and everything in it. On success the device forgets its guest id
 * and that it was ever signed in, so the next load is a first visit with a new guest.
 * Answers whether it went.
 */
export async function deleteAccount(): Promise<boolean> {
  const outcome = await post('/auth/account/delete', { confirm: 'delete' });
  if (!outcome.ok) return false;
  try {
    localStorage.removeItem(DEVICE_KEY);
  } catch {
    // Private windows have no storage. Nothing was kept, so nothing to forget.
  }
  forgetSignedIn();
  return true;
}

/** Creates a passkey for the account this device is signed in as. */
export async function registerPasskey(displayName?: string): Promise<SignInResult> {
  const options = await post<{ options: PublicKeyCredentialCreationOptionsJSON }>(
    '/auth/passkey/register/options',
  );
  if (!options.ok || !options.body) {
    return { ok: false, message: startFailureMessage(options.status, SIGNIN_PASSKEY_START_FAILED) };
  }
  try {
    const response = await startRegistration({ optionsJSON: options.body.options });
    const verified = await post<{ ok: boolean; user: Account }>('/auth/passkey/register/verify', {
      response,
      ...(displayName ? { displayName } : {}),
    });
    if (!verified.body?.ok) return { ok: false, message: 'That passkey was not accepted.' };
    // This device now has a way back in, so it never silently becomes a guest again (D190).
    rememberSignedIn();
    return {
      ok: true,
      message: 'Passkey saved. Your farm is now tied to this account, not this browser.',
      account: verified.body.user,
    };
  } catch (err) {
    return {
      ok: false,
      message: passkeyFailure(err, options.body.options.rp?.id, 'Passkey setup was cancelled.'),
    };
  }
}

/** Signs in with an existing passkey, merging this device's guest world if there is one. */
export async function signInWithPasskey(): Promise<SignInResult> {
  const options = await post<{ options: PublicKeyCredentialRequestOptionsJSON }>(
    '/auth/passkey/login/options',
  );
  if (!options.ok || !options.body) {
    return { ok: false, message: startFailureMessage(options.status, SIGNIN_START_FAILED) };
  }
  try {
    const response = await startAuthentication({ optionsJSON: options.body.options });
    const verified = await post<{
      ok: boolean;
      user: Account;
      merged: boolean;
      archivedWorldId: string | null;
    }>('/auth/passkey/login/verify', { response });
    if (!verified.body?.ok) return { ok: false, message: 'That passkey was not recognised.' };
    rememberSignedIn();
    return {
      ok: true,
      message: mergeMessage(verified.body.merged, verified.body.archivedWorldId),
      account: verified.body.user,
      merged: verified.body.merged,
      archivedWorldId: verified.body.archivedWorldId,
    };
  } catch (err) {
    return {
      ok: false,
      message: passkeyFailure(err, options.body.options.rpId, SIGNIN_PASSKEY_NOT_CHOSEN),
    };
  }
}

/**
 * What to tell the player about the guest world on this device (D8, D40). An account with
 * a free slot adopts it; an account already holding three has to put it away.
 */
function mergeMessage(merged: boolean, archivedWorldId: string | null): string {
  if (!merged) return 'Signed in.';
  return archivedWorldId
    ? 'Signed in. Your account is already at three farms, so the one on this device has been put away.'
    : 'Signed in. The farm from this device has been added to your account.';
}

/** Asks for a magic link. The player follows it from their mail. */
export async function requestMagicLink(email: string): Promise<SignInResult> {
  const token = await turnstileToken();
  const res = await call('/auth/magic', {
    method: 'POST',
    body: JSON.stringify({ email, ...(token ? { turnstileToken: token } : {}) }),
  });
  if (!res) return { ok: false, message: SIGNIN_NO_CONNECTION };
  if (classifyStatus(res.status) === 'maintenance') {
    return { ok: false, message: SIGNIN_MAINTENANCE };
  }
  if (res.status === 429) {
    return { ok: false, message: SIGNIN_TOO_MANY_LINKS };
  }
  if (!res.ok) return { ok: false, message: SIGNIN_ADDRESS_REFUSED };
  const body = (await res.json().catch(() => null)) as {
    via?: 'resend' | 'mailpit' | 'logged';
    link?: string;
    code?: string;
  } | null;
  // The local stack sends no real mail: it hands the link back so it can be opened here.
  if (body?.link) {
    return {
      ok: true,
      message:
        body.via === 'mailpit'
          ? 'No mail leaves the local stack. The link is below, and a copy is in Mailpit.'
          : 'No mail leaves the local stack. The link is below.',
      link: body.link,
      ...(body.code ? { code: body.code } : {}),
    };
  }
  return { ok: true, message: SIGNIN_CODE_SENT };
}

/**
 * Signs in with the code from the sign-in email (item 79). This is the route that works
 * inside an installed app, where a link would open the browser and leave the session there.
 * The guest world on this device merges exactly as the link's does.
 */
export async function signInWithCode(email: string, code: string): Promise<SignInResult> {
  const res = await call('/auth/magic/code', {
    method: 'POST',
    body: JSON.stringify({ email, code }),
  });
  if (!res) return { ok: false, message: SIGNIN_NO_CONNECTION };
  if (classifyStatus(res.status) === 'maintenance')
    return { ok: false, message: SIGNIN_MAINTENANCE };
  if (res.status === 429) return { ok: false, message: SIGNIN_TOO_MANY_CODES };
  if (!res.ok) return { ok: false, message: SIGNIN_CODE_REFUSED };
  const body = (await res.json().catch(() => null)) as {
    ok?: boolean;
    user?: Account;
    merged?: boolean;
    archivedWorldId?: string | null;
  } | null;
  if (!body?.ok || !body.user) return { ok: false, message: SIGNIN_CODE_REFUSED };
  rememberSignedIn();
  const merged = body.merged ?? false;
  const archivedWorldId = body.archivedWorldId ?? null;
  return {
    ok: true,
    message: mergeMessage(merged, archivedWorldId),
    account: body.user,
    merged,
    archivedWorldId,
  };
}

/**
 * What the magic link redirect left in the address bar, read once on load so the
 * player can be told what happened. Clears the query so a reload does not repeat it.
 */
export function consumeSignInOutcome(): SignInResult | null {
  const params = new URLSearchParams(location.search);
  const signin = params.get('signin');
  if (!signin) return null;
  const merged = params.get('merged') === '1';
  const archivedWorldId = params.get('archived');
  history.replaceState(null, '', location.pathname);
  if (signin === 'expired') {
    return { ok: false, message: 'That link had already been used or had expired.' };
  }
  // The magic link is an email used on this device, same as the code route (D190).
  rememberSignedIn();
  return {
    ok: true,
    merged,
    archivedWorldId,
    message: mergeMessage(merged, archivedWorldId),
  };
}

// --- Worlds ----------------------------------------------------------------------

/**
 * `GET /worlds`: the account's active worlds and which one the session is acting on (D40).
 * Null when nothing answered, which is the offline case; the Worlds card then shows what
 * it last had.
 */
export async function fetchWorlds(): Promise<WorldsResponse | null> {
  const res = await call('/worlds');
  if (!res || !res.ok) return null;
  const body = (await res.json()) as WorldsResponse;
  return body.ok ? body : null;
}

/**
 * `POST /worlds/:id/delete` (D221). Null when nothing answered; otherwise the server's own
 * answer, a refusal or a delete that stopped part way included, so the sheet can say which.
 */
export async function deleteWorld(worldId: string): Promise<DeleteWorldResponse | null> {
  const res = await call(`/worlds/${encodeURIComponent(worldId)}/delete`, { method: 'POST' });
  if (!res) return null;
  try {
    return (await res.json()) as DeleteWorldResponse;
  } catch {
    return { ok: false, error: `status ${res.status}` };
  }
}
